/**
 * Inventory.jsx — Updated
 *
 * Added vs original:
 *   - GST Rate, HSN Code, SAC Code, Barcode fields in form
 *   - Low Stock alert banner
 *   - Stock Valuation card
 *   - Type (PRODUCT/SERVICE) selector
 *   - Stock status badge with correct colors
 *   - Barcode display in table
 *
 * PASTE AT: frontend/src/pages/admin/Inventory.jsx  (REPLACE existing)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { inventoryAPI, projectsAPI } from '../../services/api';
import { Table }      from '../../components/common/Table';
import { Button }     from '../../components/common/Button';
import { Modal }      from '../../components/common/Modal';
import { Pagination } from '../../components/common/Pagination';
import { StatCard }   from '../../components/common/StatCard';
import { PageHeader } from '../../components/layout/PageHeader';
import toast from 'react-hot-toast';
import {
  PlusIcon, MagnifyingGlassIcon, PencilIcon, TrashIcon,
  AdjustmentsHorizontalIcon, ExclamationTriangleIcon,
  CubeIcon, BanknotesIcon, ClockIcon,
} from '@heroicons/react/24/outline';

const EMPTY = {
  name: '', type: 'PRODUCT', sku: '', barcode: '', category: '', description: '',
  unit: 'PCS', hsnCode: '', sacCode: '', gstRate: 18, cessRate: 0,
  taxPreference: 'TAXABLE', mrp: '', purchasePrice: '', sellingPrice: '',
  quantity: 0, minStock: 0, maxStock: '', trackInventory: true,
  warehouseLocation: '', project: '', location: '',
};
const ADJ0 = { type: 'in', quantity: '', reason: '', reference: '' };

const GST_RATES = [0, 5, 12, 18, 28];
const STOCK_COLORS = {
  'in-stock':    'bg-green-100 text-green-700',
  'low-stock':   'bg-yellow-100 text-yellow-700',
  'out-of-stock':'bg-red-100 text-red-600',
  'not-tracked': 'bg-gray-100 text-gray-500',
};

export function InventoryPage() {
  const [items, setItems]         = useState([]);
  const [projects, setProjects]   = useState([]);
  const [pg, setPg]               = useState(null);
  const [loading, setL]           = useState(true);
  const [search, setSrch]         = useState('');
  const [gstFilter, setGstF]      = useState('');
  const [typeFilter, setTypeF]    = useState('');
  const [page, setPage]           = useState(1);
  const [lowStock, setLowStock]   = useState([]);
  const [valuation, setValuation] = useState(null);
  const [stats, setStats]         = useState({ total: 0, lowCount: 0, outCount: 0 });

  const [open, setOpen]     = useState(false);
  const [adjOpen, setAdjOpen] = useState(false);
  const [editing, setEd]    = useState(null);
  const [adjItem, setAdjItem] = useState(null);
  const [form, setForm]     = useState(EMPTY);
  const [adj, setAdj]       = useState(ADJ0);
  const [saving, setSv]     = useState(false);

  // Stock history modal
  const [histOpen, setHistOpen] = useState(false);
  const [histItem, setHistItem] = useState(null);
  const [histData, setHistData] = useState([]);
  const [histLoad, setHistLoad] = useState(false);

  const load = useCallback(async () => {
    setL(true);
    try {
      const r = await inventoryAPI.getAll({ search, page, limit: 15, gstRate: gstFilter || undefined, type: typeFilter || undefined });
      setItems(r.items || r.data || []);
      setPg(r.pagination);
      setStats({
        total:    r.pagination?.total || 0,
        lowCount: (r.items || []).filter(i => i.stockStatus === 'low-stock').length,
        outCount: (r.items || []).filter(i => i.stockStatus === 'out-of-stock').length,
      });
    } catch { toast.error('Failed to load inventory'); }
    finally { setL(false); }
  }, [search, page, gstFilter, typeFilter]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    inventoryAPI.getLowStock().then(r => setLowStock(r.data || [])).catch(() => {});
    inventoryAPI.getValuation().then(r => setValuation(r.data || null)).catch(() => {});
    projectsAPI.getAll({ limit: 100 }).then(r => setProjects(r.data || [])).catch(() => {});
  }, []);

  const openCreate = () => { setEd(null); setForm(EMPTY); setOpen(true); };
  const openEdit   = item => {
    setEd(item);
    setForm({
      name:              item.name || '',
      type:              item.type || 'PRODUCT',
      sku:               item.sku || '',
      barcode:           item.barcode || '',
      category:          item.category || '',
      description:       item.description || '',
      unit:              item.unit || 'PCS',
      hsnCode:           item.hsnCode || '',
      sacCode:           item.sacCode || '',
      gstRate:           item.gstRate ?? 18,
      cessRate:          item.cessRate || 0,
      taxPreference:     item.taxPreference || 'TAXABLE',
      mrp:               item.mrp || '',
      purchasePrice:     item.purchasePrice || '',
      sellingPrice:      item.sellingPrice || '',
      quantity:          item.quantity || 0,
      minStock:          item.minStock || 0,
      maxStock:          item.maxStock || '',
      trackInventory:    item.trackInventory ?? true,
      warehouseLocation: item.warehouseLocation || '',
      project:           item.project?._id || '',
      location:          item.location || '',
    });
    setOpen(true);
  };
  const openAdj = item => { setAdjItem(item); setAdj(ADJ0); setAdjOpen(true); };

  const openHistory = async item => {
    setHistItem(item);
    setHistOpen(true);
    setHistLoad(true);
    try {
      const res = await inventoryAPI.getTransactions(item._id);
      setHistData(res.data || []);
    } catch { setHistData([]); }
    finally { setHistLoad(false); }
  };
  const del = async id => {
    if (!window.confirm('Deactivate this item?')) return;
    try { await inventoryAPI.delete(id); toast.success('Deactivated'); load(); }
    catch { toast.error('Failed'); }
  };

  const save = async e => {
    e.preventDefault(); setSv(true);
    try {
      editing
        ? await inventoryAPI.update(editing._id, form)
        : await inventoryAPI.create(form);
      toast.success(editing ? 'Updated' : 'Created');
      setOpen(false); load();
    } catch (err) { toast.error(err.message || 'Failed'); }
    finally { setSv(false); }
  };

  const saveAdj = async e => {
    e.preventDefault(); setSv(true);
    try {
      await inventoryAPI.adjustStock(adjItem._id, { ...adj, quantity: Number(adj.quantity) });
      toast.success('Stock adjusted'); setAdjOpen(false); load();
    } catch (err) { toast.error(err.message || 'Failed'); }
    finally { setSv(false); }
  };

  const s  = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const sa = (k, v) => setAdj(a => ({ ...a, [k]: v }));

  const inp   = 'w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500';
  const label = 'text-xs font-semibold text-gray-500 uppercase mb-1 block';

  const cols = [
    { header: 'Item', render: i => (
      <div>
        <p className="font-medium text-sm text-gray-900">{i.name}</p>
        <p className="text-xs text-gray-400 font-mono">{[i.sku, i.barcode].filter(Boolean).join(' · ') || '—'}</p>
        <p className="text-xs text-gray-400">{i.category || 'General'} · {i.type}</p>
      </div>
    )},
    { header: 'HSN / GST', render: i => (
      <div>
        <p className="text-sm font-mono text-gray-700 font-semibold">{i.hsnCode || i.sacCode || '—'}</p>
        <p className="text-xs text-indigo-600 font-semibold mt-0.5">
          {i.gstRate != null ? `${i.gstRate}% GST` : '—'}
        </p>
        {i.cessRate > 0 && <p className="text-xs text-orange-500">+{i.cessRate}% CESS</p>}
        <p className="text-xs text-gray-400 mt-0.5">{i.taxPreference === 'EXEMPT' ? 'Exempt' : i.type === 'SERVICE' ? 'SAC' : 'HSN'}</p>
      </div>
    )},
    { header: 'Stock', render: i => (
      <div>
        {i.trackInventory
          ? <p className="font-semibold text-gray-900 text-sm">{i.quantity} <span className="text-xs font-normal text-gray-400">{i.unit}</span></p>
          : <p className="text-xs text-gray-400">Not tracked</p>}
        {i.trackInventory && <p className="text-xs text-gray-400">Min: {i.minStock}</p>}
      </div>
    )},
    { header: 'Status', render: i => {
      const st = i.stockStatus || (i.quantity === 0 ? 'out-of-stock' : i.quantity <= i.minStock ? 'low-stock' : 'in-stock');
      return (
        <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${STOCK_COLORS[st] || ''}`}>
          {st.replace(/-/g, ' ')}
        </span>
      );
    }},
    { header: 'Prices', render: i => (
      <div className="text-xs space-y-0.5">
        <p><span className="text-gray-400">Buy: </span><span className="font-medium text-gray-700">Rs.{Number(i.purchasePrice||0).toLocaleString('en-IN')}</span></p>
        <p><span className="text-gray-400">Sell: </span><span className="font-semibold text-gray-900">Rs.{Number(i.sellingPrice||0).toLocaleString('en-IN')}</span></p>
        {i.mrp > 0 && <p><span className="text-gray-400">MRP: </span><span className="text-gray-500">Rs.{Number(i.mrp).toLocaleString('en-IN')}</span></p>}
      </div>
    )},
    { header: '', render: i => (
      <div className="flex gap-1">
        <button onClick={() => openAdj(i)} className="p-1.5 hover:bg-amber-50 rounded-lg" title="Adjust Stock">
          <AdjustmentsHorizontalIcon className="w-4 h-4 text-amber-500" />
        </button>
        <button onClick={() => openHistory(i)} className="p-1.5 hover:bg-blue-50 rounded-lg" title="Stock History">
          <ClockIcon className="w-4 h-4 text-blue-500" />
        </button>
        <button onClick={() => openEdit(i)} className="p-1.5 hover:bg-indigo-50 rounded-lg" title="Edit">
          <PencilIcon className="w-4 h-4 text-indigo-600" />
        </button>
        <button onClick={() => del(i._id)} className="p-1.5 hover:bg-red-50 rounded-lg" title="Deactivate">
          <TrashIcon className="w-4 h-4 text-red-500" />
        </button>
      </div>
    )},
  ];

  return (
    <div className="space-y-5">
      <PageHeader title="Inventory" subtitle="Products, services, stock & GST rates" />

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard title="Total Items"   value={loading ? '...' : stats.total}    color="indigo" icon={CubeIcon} />
        <StatCard title="Low Stock"     value={loading ? '...' : lowStock.length} color="amber"  icon={ExclamationTriangleIcon} />
        <StatCard title="Out of Stock"  value={loading ? '...' : stats.outCount}  color="red"    icon={ExclamationTriangleIcon} />
        <StatCard
          title="Stock Value"
          value={valuation?.summary?.totalCostValue != null
            ? `Rs.${(Number(valuation.summary.totalCostValue)/1000).toFixed(0)}K`
            : '—'}
          color="green"
          icon={BanknotesIcon}
          subtitle={valuation?.summary?.totalCostValue
            ? `Rs.${Number(valuation.summary.totalCostValue).toLocaleString('en-IN')}`
            : 'at cost price'}
        />
      </div>

      {/* Low stock alert */}
      {lowStock.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex gap-3">
          <ExclamationTriangleIcon className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-amber-800">Low Stock Alert — {lowStock.length} items</p>
            <p className="text-xs text-amber-600 mt-1">
              {lowStock.slice(0, 4).map(i => `${i.name} (${i.quantity} ${i.unit})`).join(' · ')}
              {lowStock.length > 4 && ` +${lowStock.length - 4} more`}
            </p>
          </div>
        </div>
      )}

      {/* List */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="p-4 border-b border-gray-100 flex gap-3 flex-wrap items-center justify-between">
          <div className="flex gap-3 flex-1 flex-wrap">
            <div className="relative flex-1 min-w-44">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input value={search} onChange={e => { setSrch(e.target.value); setPage(1); }}
                placeholder="Search name, SKU, barcode, HSN..."
                className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
            </div>
            <select value={gstFilter} onChange={e => setGstF(e.target.value)}
              className="px-3 py-2 border border-gray-200 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500">
              <option value="">All GST Rates</option>
              {GST_RATES.map(r => <option key={r} value={r}>{r}%</option>)}
            </select>
            <select value={typeFilter} onChange={e => setTypeF(e.target.value)}
              className="px-3 py-2 border border-gray-200 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500">
              <option value="">All Types</option>
              <option value="PRODUCT">Products</option>
              <option value="SERVICE">Services</option>
            </select>
          </div>
          <Button icon={PlusIcon} onClick={openCreate}>Add Item</Button>
        </div>

        <Table columns={cols} data={items} loading={loading} emptyMessage="No inventory items found" />
        <Pagination pagination={pg} onPageChange={setPage} />
      </div>

      {/* Create / Edit modal */}
      <Modal isOpen={open} onClose={() => setOpen(false)} title={editing ? 'Edit Item' : 'Add Inventory Item'} size="lg">
        <form onSubmit={save} className="space-y-5">
          {/* Basic */}
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className={label}>Item Name *</label>
              <input value={form.name} onChange={e => s('name', e.target.value)} placeholder="LED Panel Light" className={inp} required />
            </div>
            <div>
              <label className={label}>Type</label>
              <select value={form.type} onChange={e => s('type', e.target.value)} className={inp}>
                <option value="PRODUCT">Product (Goods)</option>
                <option value="SERVICE">Service</option>
              </select>
            </div>
            <div>
              <label className={label}>Category</label>
              <input value={form.category} onChange={e => s('category', e.target.value)} placeholder="Electrical, Civil..." className={inp} />
            </div>
            <div>
              <label className={label}>SKU</label>
              <input value={form.sku} onChange={e => s('sku', e.target.value)} placeholder="LED-001" className={inp} />
            </div>
            <div>
              <label className={label}>Barcode</label>
              <input value={form.barcode} onChange={e => s('barcode', e.target.value)} placeholder="8901234567890" className={inp} />
            </div>
            <div>
              <label className={label}>Unit</label>
              <select value={form.unit} onChange={e => s('unit', e.target.value)} className={inp}>
                {['PCS','KG','LITRE','METER','BOX','SET','TON','SQF','SQM','HOUR','DAY'].map(u => (
                  <option key={u} value={u}>{u}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={label}>Description</label>
              <input value={form.description} onChange={e => s('description', e.target.value)} placeholder="Optional description" className={inp} />
            </div>
          </div>

          {/* GST */}
          <div>
            <p className="text-xs font-bold text-indigo-600 uppercase mb-3 border-b border-indigo-100 pb-1">GST Details</p>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className={label}>{form.type === 'SERVICE' ? 'SAC Code' : 'HSN Code'}</label>
                <input
                  value={form.type === 'SERVICE' ? form.sacCode : form.hsnCode}
                  onChange={e => s(form.type === 'SERVICE' ? 'sacCode' : 'hsnCode', e.target.value)}
                  placeholder={form.type === 'SERVICE' ? '998519' : '94054090'}
                  className={inp}
                />
              </div>
              <div>
                <label className={label}>GST Rate</label>
                <select value={form.gstRate} onChange={e => s('gstRate', Number(e.target.value))} className={inp}>
                  {GST_RATES.map(r => <option key={r} value={r}>{r}%</option>)}
                </select>
              </div>
              <div>
                <label className={label}>CESS %</label>
                <input type="number" min="0" max="100" step="0.1" value={form.cessRate}
                  onChange={e => s('cessRate', e.target.value)} className={inp} />
              </div>
              <div>
                <label className={label}>Tax Preference</label>
                <select value={form.taxPreference} onChange={e => s('taxPreference', e.target.value)} className={inp}>
                  <option value="TAXABLE">Taxable</option>
                  <option value="EXEMPT">Exempt</option>
                  <option value="ZERO_RATED">Zero Rated</option>
                </select>
              </div>
              <div className="col-span-2 flex items-center gap-2 pt-5">
                <input type="checkbox" id="taxInclusive" checked={form.taxInclusive || false}
                  onChange={e => s('taxInclusive', e.target.checked)}
                  className="w-4 h-4 text-indigo-600 rounded" />
                <label htmlFor="taxInclusive" className="text-sm text-gray-600">Selling price is tax-inclusive</label>
              </div>
            </div>
          </div>

          {/* Pricing */}
          <div>
            <p className="text-xs font-bold text-indigo-600 uppercase mb-3 border-b border-indigo-100 pb-1">Pricing</p>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className={label}>MRP (₹)</label>
                <input type="number" min="0" step="0.01" value={form.mrp}
                  onChange={e => s('mrp', e.target.value)} placeholder="0.00" className={inp} />
              </div>
              <div>
                <label className={label}>Purchase Price (₹)</label>
                <input type="number" min="0" step="0.01" value={form.purchasePrice}
                  onChange={e => s('purchasePrice', e.target.value)} placeholder="0.00" className={inp} />
              </div>
              <div>
                <label className={label}>Selling Price (₹) *</label>
                <input type="number" min="0" step="0.01" value={form.sellingPrice}
                  onChange={e => s('sellingPrice', e.target.value)} placeholder="0.00" className={inp} required />
              </div>
            </div>
          </div>

          {/* Stock */}
          <div>
            <p className="text-xs font-bold text-indigo-600 uppercase mb-3 border-b border-indigo-100 pb-1">Stock</p>
            <div className="flex items-center gap-2 mb-3">
              <input type="checkbox" id="trackInv" checked={form.trackInventory}
                onChange={e => s('trackInventory', e.target.checked)}
                className="w-4 h-4 text-indigo-600 rounded" />
              <label htmlFor="trackInv" className="text-sm text-gray-600">Track inventory for this item</label>
            </div>
            {form.trackInventory && (
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className={label}>Opening Qty</label>
                  <input type="number" min="0" step="0.001" value={form.quantity}
                    onChange={e => s('quantity', e.target.value)} className={inp} />
                </div>
                <div>
                  <label className={label}>Min Stock (Alert)</label>
                  <input type="number" min="0" step="0.001" value={form.minStock}
                    onChange={e => s('minStock', e.target.value)} className={inp} />
                </div>
                <div>
                  <label className={label}>Location / Rack</label>
                  <input value={form.warehouseLocation} onChange={e => s('warehouseLocation', e.target.value)}
                    placeholder="A-12, Shelf 3" className={inp} />
                </div>
              </div>
            )}
          </div>

          <div className="flex gap-3 pt-2 border-t border-gray-100">
            <button type="button" onClick={() => setOpen(false)}
              className="px-5 py-2.5 border border-gray-200 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50">
              Cancel
            </button>
            <button type="submit" disabled={saving}
              className="flex-1 px-5 py-2.5 bg-indigo-600 rounded-xl text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50">
              {saving ? 'Saving...' : (editing ? 'Update Item' : 'Add Item')}
            </button>
          </div>
        </form>
      </Modal>

      {/* Adjust Stock modal */}
      <Modal isOpen={adjOpen} onClose={() => setAdjOpen(false)} title={`Adjust Stock — ${adjItem?.name || ''}`} size="sm">
        {adjItem && (
          <form onSubmit={saveAdj} className="space-y-4">
            <div className="bg-indigo-50 rounded-xl p-3 text-sm">
              <p className="text-gray-500">Current Stock: <span className="font-bold text-gray-900">{adjItem.quantity} {adjItem.unit}</span></p>
            </div>
            <div>
              <label className={label}>Adjustment Type</label>
              <select value={adj.type} onChange={e => sa('type', e.target.value)}
                className={inp}>
                <option value="in">Stock In (Add)</option>
                <option value="out">Stock Out (Remove)</option>
                <option value="adjustment">Set Exact Quantity</option>
              </select>
            </div>
            <div>
              <label className={label}>Quantity</label>
              <input type="number" min="0.001" step="0.001" value={adj.quantity}
                onChange={e => sa('quantity', e.target.value)} className={inp} required />
            </div>
            <div>
              <label className={label}>Reason</label>
              <input value={adj.reason} onChange={e => sa('reason', e.target.value)}
                placeholder="Purchase, Sale, Damage, Count..." className={inp} required />
            </div>
            <div>
              <label className={label}>Reference</label>
              <input value={adj.reference} onChange={e => sa('reference', e.target.value)}
                placeholder="Invoice / PO number" className={inp} />
            </div>
            <div className="flex gap-2 pt-1">
              <button type="button" onClick={() => setAdjOpen(false)}
                className="flex-1 px-4 py-2 border border-gray-200 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50">Cancel</button>
              <button type="submit" disabled={saving}
                className="flex-1 px-4 py-2 bg-indigo-600 rounded-xl text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50">
                {saving ? 'Saving...' : 'Adjust Stock'}
              </button>
            </div>
          </form>
        )}
      </Modal>
      {/* Stock History Modal */}
      <Modal isOpen={histOpen} onClose={() => setHistOpen(false)} title={`Stock History — ${histItem?.name || ''}`} size="lg">
        <div className="space-y-3">
          {/* Current stock summary */}
          {histItem && (
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="bg-indigo-50 rounded-xl p-3 text-center">
                <p className="text-xs text-gray-500 uppercase font-semibold">Current Stock</p>
                <p className="text-2xl font-bold text-indigo-600 mt-1">{histItem.quantity}</p>
                <p className="text-xs text-gray-400">{histItem.unit}</p>
              </div>
              <div className="bg-green-50 rounded-xl p-3 text-center">
                <p className="text-xs text-gray-500 uppercase font-semibold">Min Stock</p>
                <p className="text-2xl font-bold text-green-600 mt-1">{histItem.minStock || 0}</p>
                <p className="text-xs text-gray-400">Alert level</p>
              </div>
              <div className="bg-gray-50 rounded-xl p-3 text-center">
                <p className="text-xs text-gray-500 uppercase font-semibold">Stock Value</p>
                <p className="text-lg font-bold text-gray-800 mt-1">
                  Rs.{Number((histItem.quantity || 0) * (histItem.purchasePrice || 0)).toLocaleString('en-IN')}
                </p>
                <p className="text-xs text-gray-400">at cost price</p>
              </div>
            </div>
          )}

          {histLoad && (
            <div className="py-8 text-center">
              <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto"/>
            </div>
          )}

          {!histLoad && histData.length === 0 && (
            <div className="py-8 text-center text-gray-400 text-sm">No stock movements recorded yet</div>
          )}

          {!histLoad && histData.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    {['Date', 'Type', 'Qty', 'Before', 'After', 'Reason', 'Reference', 'By'].map(h => (
                      <th key={h} className="px-3 py-2.5 text-xs font-semibold text-gray-500 uppercase text-left">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {histData.map((tx, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="px-3 py-2.5 text-xs text-gray-500 whitespace-nowrap">
                        {tx.createdAt ? new Date(tx.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                      </td>
                      <td className="px-3 py-2.5">
                        <span className={`text-xs px-2 py-1 rounded-lg font-semibold ${
                          tx.type === 'in' ? 'bg-green-100 text-green-700' :
                          tx.type === 'out' ? 'bg-red-100 text-red-700' :
                          'bg-gray-100 text-gray-600'
                        }`}>
                          {tx.type === 'in' ? '↑ IN' : tx.type === 'out' ? '↓ OUT' : '~ ADJ'}
                        </span>
                      </td>
                      <td className={`px-3 py-2.5 font-bold ${tx.type === 'in' ? 'text-green-600' : tx.type === 'out' ? 'text-red-600' : 'text-gray-700'}`}>
                        {tx.type === 'in' ? '+' : tx.type === 'out' ? '-' : ''}{tx.quantity}
                      </td>
                      <td className="px-3 py-2.5 text-gray-500">{tx.previousQuantity ?? '—'}</td>
                      <td className="px-3 py-2.5 font-semibold text-gray-800">{tx.newQuantity ?? '—'}</td>
                      <td className="px-3 py-2.5 text-gray-600 max-w-24 truncate">{tx.reason || '—'}</td>
                      <td className="px-3 py-2.5 font-mono text-xs text-indigo-600">{tx.reference || '—'}</td>
                      <td className="px-3 py-2.5 text-xs text-gray-400">{tx.performedBy?.name || 'System'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
}
